import { memo, useCallback, useMemo } from "react";

import { DiscountRoomConditionType } from "@/lib/gql/__generated__/graphql";

import { ExternalLink } from "@/components/ui/button/external-link";
import type { FormOptionType } from "@/components/ui/form/_types/type";
import { RadioGroupField } from "@/components/ui/form/fields/radio-field/radio-group";

import { FormFieldLayout } from "../../layouts";

type Props = {
	// 部屋の適用条件
	roomConditionType: DiscountRoomConditionType;
	// 部屋の適用条件の変更
	onChangeRoomConditionType: (value: DiscountRoomConditionType) => void;
};

const NOTION_URL = "https://chillnn.notion.site/1bafffe15c57807e9c0fc17ba027be5a";

const SameRoomOnlyOptions: FormOptionType[] = [
	{
		id: "any-room",
		label: "連泊期間を通して「複数種類の部屋を予約している場合」でも適用する",
		value: DiscountRoomConditionType.AnyRoom,
	},
	{
		id: "same-room-only",
		label: "連泊期間を通して「全ての日程で同じ種類の部屋を予約している場合」に適用する",
		value: DiscountRoomConditionType.SameRoomOnly,
	},
] satisfies FormOptionType[];

export const RoomConditionTypeFormFieldComposition = memo(({ roomConditionType, onChangeRoomConditionType }: Props) => {
	/**
	 * 部屋の適用条件の変更
	 */
	const handleChangeRoomConditionType = useCallback(
		(value: string) => {
			onChangeRoomConditionType(value as DiscountRoomConditionType);
		},
		[onChangeRoomConditionType],
	);

	const currentValue = useMemo(() => roomConditionType, [roomConditionType]);

	return (
		<FormFieldLayout title="">
			<RadioGroupField
				value={currentValue}
				options={SameRoomOnlyOptions}
				handleValueChange={handleChangeRoomConditionType}
			/>
			<div className="mt-2">
				<p className="whitespace-pre-wrap text-sm text-caption-50">
					{`「連泊期間を通して全ての日程で同じ部屋を予約している場合に適用する」を選択すると、連泊期間中すべての日程で同じ部屋を予約している場合のみ連泊ディスカウントが適用されます。\n詳しくは`}
					<ExternalLink href={NOTION_URL} text="こちら" className="text-sm" />
					{`をご覧ください。`}
				</p>
			</div>
		</FormFieldLayout>
	);
});

RoomConditionTypeFormFieldComposition.displayName = "RoomConditionTypeFormFieldComposition";
